import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, MapPin, User, X, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { robotService } from '../services/robotService';
import { messageService } from '../services/messageService';
import { Robot } from '../types';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import RobotLogo from '../components/RobotLogo';
import { C } from '../design';
import toast from 'react-hot-toast';

const DashboardPage: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [myRobots, setMyRobots] = useState<Robot[]>([]);
  const [loading, setLoading] = useState(true);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [editingRobot, setEditingRobot] = useState<Robot | null>(null);
  const [editForm, setEditForm] = useState({ name: '', description: '', price: '', isAvailable: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!currentUser) return;
    const fetchData = async () => {
      try {
        const robots = await robotService.getRobotsByOwner(currentUser.uid);
        setMyRobots(robots);
      } catch (error) { console.error('Error fetching data:', error); }
      finally { setLoading(false); }
    };
    fetchData();
    // Use real-time snapshot directly for unread count (avoids cache inconsistency)
    const unsubscribe = messageService.subscribeToMessages(currentUser.uid, (messages) => {
      const count = messages.filter(m => !m.isRead).length;
      setUnreadMessages(count);
    });
    return () => unsubscribe();
  }, [currentUser]);

  const openEdit = (robot: Robot) => {
    setEditingRobot(robot);
    setEditForm({ name: robot.name, description: robot.description, price: String(robot.price), isAvailable: robot.isAvailable });
  };

  const saveEdit = async () => {
    if (!editingRobot) return;
    setSaving(true);
    try {
      await robotService.updateRobot(editingRobot.id, {
        name: editForm.name,
        description: editForm.description,
        price: Number(editForm.price),
        isAvailable: editForm.isAvailable,
      });
      setMyRobots(prev => prev.map(r => r.id === editingRobot.id ? { ...r, name: editForm.name, description: editForm.description, price: Number(editForm.price), isAvailable: editForm.isAvailable } : r));
      setEditingRobot(null);
      toast.success('Robot updated!');
    } catch { toast.error('Failed to update robot'); }
    finally { setSaving(false); }
  };

  const handleToggleAvailability = async (robot: Robot) => {
    try {
      const next = !robot.isAvailable;
      await robotService.updateRobot(robot.id, { isAvailable: next });
      setMyRobots(prev => prev.map(r => r.id === robot.id ? { ...r, isAvailable: next } : r));
      toast.success(next ? 'Robot activated' : 'Robot deactivated');
    } catch { toast.error('Failed to update robot'); }
  };

  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDelete = async (robot: Robot) => {
    if (deletingId === robot.id) {
      // Second click = confirm
      try {
        await robotService.deleteRobot(robot.id);
        setMyRobots(prev => prev.filter(r => r.id !== robot.id));
        toast.success(`"${robot.name}" deleted`);
      } catch { toast.error('Failed to delete robot'); }
      finally { setDeletingId(null); }
    } else {
      // First click = arm
      setDeletingId(robot.id);
      setTimeout(() => setDeletingId(prev => prev === robot.id ? null : prev), 3000);
    }
  };

  const stats = [
    { label: 'My Robots', value: myRobots.length.toString(), highlight: false },
    { label: 'Active Rentals', value: '0', highlight: false },
    { label: 'Total Rentals', value: '0', highlight: false },
    { label: 'Unread Messages', value: unreadMessages.toString(), highlight: unreadMessages > 0 },
  ];

  const tabs = [
    { id: 'overview', name: 'Overview' },
    { id: 'my-robots', name: 'My Robots' },
    { id: 'rentals', name: 'Rentals' },
    { id: 'messages', name: 'Messages' },
  ];

  const cardStyle: React.CSSProperties = { background: C.pureWhite, border: `1px solid ${C.gray100}`, borderRadius: 12, padding: 24 };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            {/* Stats */}
            <div className="m-stat-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
              {stats.map((stat) => (
                <div key={stat.label} style={{
                  ...cardStyle,
                  ...(stat.highlight ? { background: "#fef2f2", border: "1px solid #fecaca" } : {}),
                  cursor: stat.highlight ? "pointer" : "default",
                }} onClick={() => { if (stat.highlight) setActiveTab('messages'); }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: stat.highlight ? "#ef4444" : C.gray400, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>
                    {stat.label}
                    {stat.highlight && <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: "#ef4444", marginLeft: 6, verticalAlign: "middle" }} />}
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: stat.highlight ? "#ef4444" : C.black }}>{stat.value}</div>
                </div>
              ))}
            </div>
            {/* Recent */}
            <div style={cardStyle}>
              <h3 style={{ fontSize: 18, fontWeight: 500, marginBottom: 20, letterSpacing: "-0.01em" }}>Recent Activity</h3>
              {myRobots.length === 0 ? (
                <p style={{ fontSize: 14, color: C.gray400, textAlign: "center", padding: "32px 0" }}>No robots listed yet. Create your first listing!</p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {myRobots.slice(0, 3).map((robot) => (
                    <div key={robot.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", background: C.gray50, borderRadius: 10 }}>
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 500 }}>{robot.name}</div>
                        <div style={{ fontSize: 13, color: C.gray400 }}>{robot.category} &middot; ${robot.price}/day</div>
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 500, color: robot.isAvailable ? C.blue : C.gray400, padding: "4px 12px", borderRadius: 100, border: `1px solid ${robot.isAvailable ? C.blue : C.gray200}` }}>
                        {robot.isAvailable ? 'Available' : 'Rented'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case 'my-robots':
        return (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <h3 style={{ fontSize: 18, fontWeight: 500, letterSpacing: "-0.01em" }}>My Robots</h3>
              <Link to="/create-robot" style={{ padding: "10px 24px", borderRadius: 100, fontSize: 14, fontWeight: 500, background: C.blue, color: C.pureWhite, border: "none", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}>
                <Plus size={16} /> Add Robot
              </Link>
            </div>
            {loading ? (
              <p style={{ color: C.gray400, textAlign: "center", padding: 32 }}>Loading...</p>
            ) : myRobots.length === 0 ? (
              <div style={{ ...cardStyle, textAlign: "center", padding: "48px 24px" }}>
                <RobotLogo color={C.gray300} size={36} />
                <p style={{ fontSize: 15, color: C.gray400, marginTop: 16, marginBottom: 20 }}>You haven't listed any robots yet.</p>
                <Link to="/create-robot" style={{ padding: "10px 24px", borderRadius: 100, fontSize: 14, fontWeight: 500, background: C.blue, color: C.pureWhite, textDecoration: "none" }}>List Your First Robot</Link>
              </div>
            ) : (
              <div className="m-grid-1" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                {myRobots.map((robot) => (
                  <div key={robot.id} style={cardStyle}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{robot.name}</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13, color: C.gray400 }}><MapPin size={12} /> {robot.location}</div>
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 500, color: robot.isAvailable ? C.blue : C.gray400, padding: "4px 12px", borderRadius: 100, border: `1px solid ${robot.isAvailable ? C.blue : C.gray200}` }}>
                        {robot.isAvailable ? 'Available' : 'Rented'}
                      </span>
                    </div>
                    <div className="m-spec-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, fontSize: 13, marginBottom: 16 }}>
                      <div><span style={{ color: C.gray400 }}>Rate:</span><div style={{ fontWeight: 500, marginTop: 2 }}>${robot.price}/day</div></div>
                      <div><span style={{ color: C.gray400 }}>Rentals:</span><div style={{ fontWeight: 500, marginTop: 2 }}>{robot.totalRentals}</div></div>
                      <div><span style={{ color: C.gray400 }}>Category:</span><div style={{ fontWeight: 500, marginTop: 2 }}>{robot.category}</div></div>
                    </div>
                    <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                      <button onClick={() => openEdit(robot)} style={{ flex: 1, padding: "10px 0", borderRadius: 100, fontSize: 13, fontWeight: 500, background: "transparent", color: C.gray700, border: `1.5px solid ${C.gray200}`, cursor: "pointer", fontFamily: "inherit" }}>Edit</button>
                      <Link to={`/robots/${robot.id}`} style={{ flex: 1, padding: "10px 0", borderRadius: 100, fontSize: 13, fontWeight: 500, background: "transparent", color: C.gray700, border: `1.5px solid ${C.gray200}`, textDecoration: "none", textAlign: "center" }}>View</Link>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => handleToggleAvailability(robot)} style={{
                        flex: 1, padding: "10px 0", borderRadius: 100, fontSize: 13, fontWeight: 500,
                        background: "transparent", border: `1.5px solid ${robot.isAvailable ? C.gray200 : C.blue}`,
                        color: robot.isAvailable ? C.gray500 : C.blue, cursor: "pointer", fontFamily: "inherit",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                      }}>
                        {robot.isAvailable ? <><ToggleLeft size={14} /> Deactivate</> : <><ToggleRight size={14} /> Activate</>}
                      </button>
                      <button onClick={() => handleDelete(robot)} style={{
                        flex: 1, padding: "10px 0", borderRadius: 100, fontSize: 13, fontWeight: 500,
                        background: deletingId === robot.id ? "#ef4444" : "transparent",
                        border: `1.5px solid ${deletingId === robot.id ? "#ef4444" : C.gray200}`,
                        color: deletingId === robot.id ? C.pureWhite : "#ef4444", cursor: "pointer", fontFamily: "inherit",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 6, transition: "all 0.2s",
                      }}>
                        <Trash2 size={14} /> {deletingId === robot.id ? 'Confirm Delete' : 'Delete'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'rentals':
        return (
          <div style={cardStyle}>
            <h3 style={{ fontSize: 18, fontWeight: 500, marginBottom: 16 }}>Rental History</h3>
            <p style={{ fontSize: 14, color: C.gray400, textAlign: "center", padding: 32 }}>Rental system coming soon!</p>
          </div>
        );

      case 'messages':
        return (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <h3 style={{ fontSize: 18, fontWeight: 500 }}>Messages</h3>
              <Link to="/messages" style={{ padding: "10px 24px", borderRadius: 100, fontSize: 14, fontWeight: 500, background: C.blue, color: C.pureWhite, textDecoration: "none" }}>View All</Link>
            </div>
            <div style={cardStyle}>
              {unreadMessages > 0 ? (
                <div style={{ textAlign: "center", padding: 24 }}>
                  <p style={{ fontSize: 15, marginBottom: 16 }}>You have {unreadMessages} unread message{unreadMessages === 1 ? '' : 's'}</p>
                  <Link to="/messages" style={{ fontSize: 14, fontWeight: 500, color: C.blue, textDecoration: "none", borderBottom: `1px solid ${C.blue}`, paddingBottom: 2 }}>View Messages &rarr;</Link>
                </div>
              ) : (
                <p style={{ fontSize: 14, color: C.gray400, textAlign: "center", padding: 24 }}>No new messages</p>
              )}
            </div>
          </div>
        );

      default: return null;
    }
  };

  return (
    <div style={{ fontFamily: "'Satoshi', sans-serif", color: C.black }}>
      <Navbar />

      {/* Header */}
      <section className="m-section-page-hero" style={{ background: C.black, color: C.pureWhite, padding: "120px 48px 48px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 500, letterSpacing: "0.18em", textTransform: "uppercase", color: C.gray400, marginBottom: 16 }}>Dashboard</div>
            <h1 style={{ fontSize: "clamp(30px, 3.5vw, 44px)", fontWeight: 400, letterSpacing: "-0.025em", lineHeight: 1.15 }}>
              Welcome back, {currentUser?.displayName?.split(' ')[0] || 'User'}.
            </h1>
          </div>
          <button onClick={logout} style={{ fontSize: 14, fontWeight: 500, color: C.gray400, cursor: "pointer", background: "none", border: "none", fontFamily: "inherit" }}>Logout</button>
        </div>
      </section>

      <section className="m-section" style={{ background: C.gray50, padding: "48px 48px 100px" }}>
        <div className="m-sidebar-layout" style={{ maxWidth: 1100, margin: "0 auto", display: "grid", gridTemplateColumns: "220px 1fr", gap: 32 }}>
          {/* Sidebar */}
          <div>
            <div style={cardStyle}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, paddingBottom: 16, borderBottom: `1px solid ${C.gray100}` }}>
                <div style={{ width: 40, height: 40, borderRadius: "50%", background: C.gray50, border: `1px solid ${C.gray100}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <User size={18} color={C.gray400} />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{currentUser?.displayName || 'User'}</div>
                  <div style={{ fontSize: 12, color: C.gray400 }}>{currentUser?.email}</div>
                </div>
              </div>
              <nav style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {tabs.map((tab) => (
                  <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "10px 14px", borderRadius: 8, fontSize: 14, fontWeight: 500,
                      background: activeTab === tab.id ? C.gray50 : "transparent",
                      color: activeTab === tab.id ? C.black : C.gray500,
                      border: "none", cursor: "pointer", fontFamily: "inherit",
                      transition: "all 0.15s", textAlign: "left", width: "100%",
                    }}>
                    {tab.name}
                    {tab.id === 'messages' && unreadMessages > 0 && (
                      <span style={{ background: "#ef4444", color: C.pureWhite, fontSize: 10, fontWeight: 700, borderRadius: 100, padding: "2px 8px" }}>{unreadMessages}</span>
                    )}
                  </button>
                ))}
                <Link to="/profile" style={{
                  display: "block", padding: "10px 14px", borderRadius: 8,
                  fontSize: 14, fontWeight: 500, color: C.gray500,
                  textDecoration: "none", transition: "all 0.15s",
                }}>Edit Profile</Link>
              </nav>
            </div>
          </div>

          {/* Content */}
          <div>{renderTabContent()}</div>
        </div>
      </section>

      {/* Edit Modal */}
      {editingRobot && (
        <div style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setEditingRobot(null)}>
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)" }} />
          <div style={{ position: "relative", background: C.pureWhite, borderRadius: 16, padding: 32, width: "100%", maxWidth: 480, boxShadow: "0 24px 80px rgba(0,0,0,0.15)" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <h2 style={{ fontSize: 20, fontWeight: 500 }}>Edit Robot</h2>
              <button onClick={() => setEditingRobot(null)} style={{ background: "none", border: "none", cursor: "pointer", color: C.gray400 }}><X size={20} /></button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label style={{ display: "block", fontSize: 14, fontWeight: 500, marginBottom: 6 }}>Name</label>
                <input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                  style={{ width: "100%", padding: "10px 14px", border: `1.5px solid ${C.gray200}`, borderRadius: 10, fontSize: 15, fontFamily: "inherit", outline: "none" }} />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 14, fontWeight: 500, marginBottom: 6 }}>Description</label>
                <textarea value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} rows={3}
                  style={{ width: "100%", padding: "10px 14px", border: `1.5px solid ${C.gray200}`, borderRadius: 10, fontSize: 15, fontFamily: "inherit", outline: "none", resize: "vertical" }} />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 14, fontWeight: 500, marginBottom: 6 }}>Daily Rate ($)</label>
                <input type="number" value={editForm.price} onChange={e => setEditForm(f => ({ ...f, price: e.target.value }))}
                  style={{ width: "100%", padding: "10px 14px", border: `1.5px solid ${C.gray200}`, borderRadius: 10, fontSize: 15, fontFamily: "inherit", outline: "none" }} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <button onClick={() => setEditForm(f => ({ ...f, isAvailable: !f.isAvailable }))}
                  style={{ width: 40, height: 22, borderRadius: 100, border: "none", cursor: "pointer", position: "relative", transition: "background 0.2s",
                    background: editForm.isAvailable ? C.blue : C.gray200 }}>
                  <div style={{ width: 16, height: 16, borderRadius: "50%", background: C.pureWhite, position: "absolute", top: 3, transition: "left 0.2s",
                    left: editForm.isAvailable ? 21 : 3 }} />
                </button>
                <span style={{ fontSize: 14, fontWeight: 500, color: editForm.isAvailable ? C.black : C.gray400 }}>
                  {editForm.isAvailable ? 'Available' : 'Unavailable'}
                </span>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 28, justifyContent: "flex-end" }}>
              <button onClick={() => setEditingRobot(null)}
                style={{ padding: "10px 24px", borderRadius: 100, fontSize: 14, fontWeight: 500, background: "transparent", color: C.gray700, border: `1.5px solid ${C.gray200}`, cursor: "pointer", fontFamily: "inherit" }}>
                Cancel
              </button>
              <button onClick={saveEdit} disabled={saving}
                style={{ padding: "10px 24px", borderRadius: 100, fontSize: 14, fontWeight: 500, background: saving ? C.gray300 : C.blue, color: C.pureWhite, border: "none", cursor: saving ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default DashboardPage;
